import React, { useEffect, useRef, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost } from "../drawings/_drawingsCommon";
import SyncStatusBadge from "./SyncStatusBadge";

const W = 1400, H = 900;

export default function SLDEditor({ projectId }) {
  const [layout, setLayout] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pendingDrop, setPendingDrop] = useState(null);
  const [customTag, setCustomTag] = useState("");
  const [feederMode, setFeederMode] = useState(false);
  const [feederFrom, setFeederFrom] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [activeSheet, setActiveSheet] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [viewZoom, setViewZoom] = useState(1);
  const [viewPan, setViewPan] = useState({ x: 0, y: 0 });
  const svgRef = useRef();
  const dragSaveTimer = useRef(null);

  const load = async () => {
    try {
      const [l, s] = await Promise.all([
        api.get(`/projects/${projectId}/sld/layout`),
        api.get(`/projects/${projectId}/sld/symbols`),
      ]);
      setLayout(l.data);
      setSymbols(s.data.symbols);
      // Default to first sheet if none selected
      if (!activeSheet) {
        const sheets = l.data?.sheets || [];
        if (sheets.length > 0) setActiveSheet(sheets[0].sheet_no);
      }
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { load(); }, [projectId]);

  // Mouse-wheel zoom (anchored on cursor position)
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = svg.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.15 : (1 / 1.15);
      setViewZoom((z) => {
        const newZ = Math.max(0.2, Math.min(5, z * factor));
        setViewPan((p) => {
          const worldX = cx / z - p.x;
          const worldY = cy / z - p.y;
          return { x: cx / newZ - worldX, y: cy / newZ - worldY };
        });
        return newZ;
      });
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, []);

  // Esc exits fullscreen
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && fullscreen) setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  const addSheet = async () => {
    const sheets = layout?.sheets || [];
    const n = sheets.length + 1;
    const sheet_no = `SLD-Aux-${String(n).padStart(3, "0")}`;
    const title = window.prompt("Sheet title:", `Auxiliary ${n}`);
    if (title === null) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/sld/sheets`,
                                                  { sheet_no, title });
      setLayout((l) => ({ ...l, sheets: data.sheets }));
      setActiveSheet(sheet_no);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const deleteSheet = async () => {
    if (!activeSheet) return;
    if (!window.confirm(
        `Delete sheet "${activeSheet}" and ALL its equipment/feeders?`)) return;
    try {
      const { data } = await api.delete(
        `/projects/${projectId}/sld/sheets/${encodeURIComponent(activeSheet)}`);
      setLayout((l) => ({
        ...l,
        sheets: data.sheets,
        electrical_equipment: (l.electrical_equipment || []).filter(
          (e) => e.sheet !== activeSheet),
        feeders: (l.feeders || []).filter((f) => f.sheet !== activeSheet),
      }));
      setActiveSheet(data.sheets[0]?.sheet_no || null);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const renameSheet = async () => {
    if (!activeSheet) return;
    const cur = (layout?.sheets || []).find((s) => s.sheet_no === activeSheet);
    const newTitle = window.prompt("New title:", cur?.title || activeSheet);
    if (newTitle === null) return;
    try {
      const { data } = await api.put(
        `/projects/${projectId}/sld/sheets/${encodeURIComponent(activeSheet)}`,
        { sheet_no: activeSheet, title: newTitle });
      setLayout((l) => ({ ...l, sheets: data.sheets }));
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const _svgPoint = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    return { x: (clientX - rect.left) * (W / rect.width), y: (clientY - rect.top) * (H / rect.height) };
  };
  const startDrag = (item, e) => {
    if (pendingDrop || feederMode) return;
    e.stopPropagation(); e.preventDefault();
    const p = _svgPoint(e.clientX, e.clientY);
    setSelected(item);
    setDragging({ item, sx: p.x, sy: p.y, ox: item.x, oy: item.y });
  };
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const p = _svgPoint(e.clientX, e.clientY);
      dragging.item.x = Math.round((dragging.ox + (p.x - dragging.sx)) / 10) * 10;
      dragging.item.y = Math.round((dragging.oy + (p.y - dragging.sy)) / 10) * 10;
      setLayout((l) => ({ ...l }));
    };
    const onUp = () => {
      const it = dragging.item;
      setDragging(null);
      clearTimeout(dragSaveTimer.current);
      dragSaveTimer.current = setTimeout(() => {
        api.put(`/projects/${projectId}/sld/equipment/${it.id}`,
                { x: it.x, y: it.y }).catch(() => {});
      }, 150);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, projectId]);

  const importFromRun = async () => {
    if (!window.confirm("Import an SLD from the latest AI run?\n• 1 incoming transformer + 1 main bus\n• A motor + feeder per pump / compressor / fan\nExisting items kept (merge mode).")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/sld/import-from-run`,
        { mode: "merge" });
      await load();
      alert(`Imported · ${data.summary.motors_added} motor(s) · ${data.summary.loads_seen} loads detected`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setBusy(false); }
  };

  const syncBackToRun = async () => {
    if (!window.confirm("Push current electrical SLD (transformers, buses, loads, feeders, panels) back to latest run?\nDownstream consumers (load schedule, etc.) will use the manual SLD.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/sld/sync-back-to-run`,
        { mode: "merge" });
      const s = data.summary || {};
      alert(`Synced to run ${data.run_id}:\n  Transformers: ${s.transformers}\n  Buses: ${s.buses}\n  Loads: ${s.loads}\n  Feeders: ${s.feeders}\n  Panels: ${s.panels}`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setBusy(false); }
  };

  const onCanvasClick = async (e) => {
    if (!pendingDrop || dragging) return;
    const { x, y } = _svgPoint(e.clientX, e.clientY);
    try {
      const { data } = await api.post(`/projects/${projectId}/sld/equipment`,
        { palette_id: pendingDrop.id, x, y, tag: customTag.trim() || undefined,
          sheet: activeSheet || "SLD-Main-001" });
      setLayout((l) => ({ ...l, electrical_equipment: [...(l.electrical_equipment || []), data.equipment],
                          validation: data.validation }));
      setPendingDrop(null);
      setCustomTag("");
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const onItemClick = async (item) => {
    if (feederMode) {
      if (!feederFrom) {
        setFeederFrom(item.tag);
      } else if (feederFrom !== item.tag) {
        try {
          const { data } = await api.post(`/projects/${projectId}/sld/feeders`,
            { from_equipment: feederFrom, to_equipment: item.tag,
              load_kW: 22, power_factor: 0.85,
              sheet: activeSheet || "SLD-Main-001",
              cable: { type: "XLPE_AL", size_mm2: 70, cores: "3.5C", length_m: 100 } });
          setLayout((l) => ({ ...l, feeders: [...(l.feeders || []), data.feeder], validation: data.validation }));
        } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
        setFeederFrom(null); setFeederMode(false);
      }
    } else {
      setSelected(item);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete ${item.tag}?`)) return;
    try {
      await api.delete(`/projects/${projectId}/sld/equipment/${item.id}`);
      setSelected(null);
      load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const validate = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/sld/validate`);
      setLayout((l) => ({ ...l, validation: data.validation }));
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  if (!layout) return <div style={{ padding: 24, color: "var(--pf-muted)" }}>Loading SLD Editor…</div>;

  // Filter by active sheet — only items on this sheet are visible
  const onSheet = (e) => !activeSheet
      || (e.sheet || "SLD-Main-001") === activeSheet;
  const sheetEquipment = (layout.electrical_equipment || []).filter(onSheet);
  const sheetFeeders   = (layout.feeders || []).filter(onSheet);
  const equipmentByTag = Object.fromEntries(sheetEquipment.map((e) => [e.tag, e]));
  const validation = layout.validation || [];
  const sheets = layout.sheets || [];

  // Group symbols by category
  const symbolsByCat = {};
  symbols.forEach((s) => { (symbolsByCat[s.category] ||= []).push(s); });

  const shellStyle = fullscreen ? {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "var(--pf-bg)", padding: 8,
    display: "grid",
    gridTemplateColumns: "220px 1fr 280px", gap: 8,
  } : {
    display: "grid", gridTemplateColumns: "220px 1fr 280px", gap: 8,
    height: "calc(100vh - 280px)", minHeight: 700,
  };

  return (
    <div data-testid="sld-editor" style={shellStyle}>
      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        {Object.entries(symbolsByCat).map(([cat, items]) => (
          <div key={cat} style={{ marginBottom: 10 }}>
            <div style={{ ...label, marginBottom: 4 }}>{cat}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {items.map((s) => (
                <button key={s.id} data-testid={`sld-pal-${s.id}`}
                  onClick={() => { setPendingDrop(s); setFeederMode(false); }}
                  style={{ ...btnGhost, padding: "4px 8px", textAlign: "left", fontSize: 10,
                           background: pendingDrop?.id === s.id ? "rgba(242,107,31,.20)" : "transparent",
                           borderColor: pendingDrop?.id === s.id ? "#F26B1F" : "var(--pf-border)" }}>{s.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden",
                          display: "flex", flexDirection: "column" }}>
        {/* Sheet switcher tabs */}
        <div data-testid="sld-sheet-tabs"
              style={{ display: "flex", gap: 4, padding: "6px 8px",
                          background: "var(--pf-surface)",
                          borderBottom: "1px solid var(--pf-border)",
                          alignItems: "center", flexWrap: "wrap",
                          overflowX: "auto" }}>
           {sheets.map((sh) => (
              <button key={sh.sheet_no}
                      data-testid={`sld-sheet-tab-${sh.sheet_no}`}
                      onClick={() => { setActiveSheet(sh.sheet_no);
                                            setSelected(null); }}
                      style={{
                         background: sh.sheet_no === activeSheet
                            ? "#F26B1F" : "transparent",
                         color: sh.sheet_no === activeSheet
                            ? "#fff" : "var(--pf-text)",
                         border: `1px solid ${
                            sh.sheet_no === activeSheet
                               ? "#F26B1F" : "var(--pf-border)"}`,
                         padding: "4px 12px", borderRadius: 3,
                         cursor: "pointer", fontSize: 11,
                         fontWeight: sh.sheet_no === activeSheet ? 700 : 400,
                         whiteSpace: "nowrap",
                         fontFamily: "monospace" }}>
                 {sh.sheet_no}
                 {sh.title && (
                    <span style={{ marginLeft: 6, fontSize: 10,
                                       opacity: 0.8 }}>{sh.title}</span>
                 )}
              </button>
           ))}
           <button data-testid="sld-add-sheet" onClick={addSheet}
                   title="Add new SLD sheet"
                   style={{ ...btnGhost, padding: "4px 10px", fontSize: 11 }}>
              + Sheet
           </button>
           {activeSheet && (
              <>
                 <button data-testid="sld-rename-sheet" onClick={renameSheet}
                         title="Rename current sheet"
                         style={{ ...btnGhost, padding: "4px 8px",
                                       fontSize: 11 }}>
                    ✎
                 </button>
                 {sheets.length > 1 && (
                    <button data-testid="sld-delete-sheet"
                            onClick={deleteSheet}
                            title="Delete current sheet"
                            style={{ ...btnGhost, padding: "4px 8px",
                                          fontSize: 11, color: "#ef4444" }}>
                       🗑
                    </button>
                 )}
              </>
           )}
           <span style={{ flex: 1 }} />
           <button data-testid="sld-fullscreen"
                   onClick={() => setFullscreen((v) => !v)}
                   title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
                   style={{ ...btnGhost, padding: "4px 10px",
                                 fontSize: 11 }}>
              {fullscreen ? "⮌ Exit" : "⛶ Fullscreen"}
           </button>
        </div>
        <div style={{ display: "flex", gap: 6, padding: 6, background: "var(--pf-surface-2)", borderBottom: "1px solid var(--pf-border)", flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: pendingDrop || feederMode ? "#F26B1F" : "var(--pf-muted)", fontWeight: 600 }}>
            {pendingDrop ? `Click to drop ${pendingDrop.label}` :
             feederMode ? (feederFrom ? `Click destination (from: ${feederFrom})` : "Click source equipment") :
             dragging ? "Dragging…" :
             "Pick a symbol · Add Feeder · or drag any placed item to move"}
          </span>
          {pendingDrop && (
            <input data-testid="sld-custom-tag" placeholder="Custom tag (optional)"
              value={customTag} onChange={(e) => setCustomTag(e.target.value)}
              style={{ width: 160, padding: "3px 6px", fontSize: 11, fontFamily: "monospace",
                       background: "#fff", color: "#222",
                       border: "1px solid var(--pf-border)", borderRadius: 4 }} />
          )}
          <span style={{ flex: 1 }} />
          <SyncStatusBadge projectId={projectId} editorName="sld_editor" />
          <button data-testid="sld-import-run" style={btnGhost} onClick={importFromRun} disabled={busy}>
            📥 Import from latest run
          </button>
          <button data-testid="sld-sync-back-run" style={btnGhost} onClick={syncBackToRun} disabled={busy}>
            ↗ Sync to run
          </button>
          <button data-testid="sld-feeder-mode" style={feederMode ? btn : btnGhost}
            onClick={() => { setFeederMode(!feederMode); setFeederFrom(null); setPendingDrop(null); }}>
            {feederMode ? "Cancel Feeder" : "Add Feeder"}
          </button>
          <button data-testid="sld-validate" style={btnGhost} onClick={validate}>Validate</button>
        </div>
        <svg ref={svgRef}
             viewBox={`0 0 ${W} ${H}`}
             onClick={onCanvasClick}
             data-testid="sld-canvas"
             style={{ width: "100%", height: "calc(100% - 40px)", background: "#fafafa", cursor: pendingDrop ? "crosshair" : "default" }}>
          <defs>
            <pattern id="sldGrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e0e0e0" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width={W} height={H} fill="url(#sldGrid)" />
          <g transform={`translate(${viewPan.x * viewZoom} ${viewPan.y * viewZoom}) scale(${viewZoom})`}>
          {/* Feeders (active sheet only) */}
          {sheetFeeders.map((f) => {
            const a = equipmentByTag[f.from_equipment], b = equipmentByTag[f.to_equipment];
            if (!a || !b) return null;
            return <g key={f.id} data-testid={`sld-feeder-${f.id}`}>
              <polyline points={`${a.x},${a.y} ${b.x},${a.y} ${b.x},${b.y}`}
                fill="none" stroke="#222" strokeWidth={2} />
              <text x={(a.x + b.x) / 2} y={a.y - 6} fontSize={9} fontFamily="monospace"
                    textAnchor="middle" fill="#444">{f.id} · {f.cable?.size_mm2}mm² · {f.load_kW}kW</text>
            </g>;
          })}

          {/* Equipment (active sheet only) */}
          {sheetEquipment.map((e) => (
            <SLDSymbol key={e.id} item={e} selected={selected?.id === e.id}
              onMouseDown={(ev) => startDrag(e, ev)}
              onClick={(ev) => { ev.stopPropagation(); onItemClick(e); }} />
          ))}
          </g>
        </svg>
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        {selected ? (
          <div data-testid="sld-properties">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <strong style={{ color: "#F26B1F" }}>{selected.tag}</strong>
              <button style={{ ...btnGhost, color: "#ef4444", padding: "3px 8px", fontSize: 10 }}
                      onClick={() => deleteItem(selected)} data-testid="sld-delete-btn">Delete</button>
            </div>
            <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>{selected.palette_id} · {selected.category}</div>
            <pre style={{ padding: 6, background: "var(--pf-surface-2)", borderRadius: 4, fontSize: 10, fontFamily: "monospace", whiteSpace: "pre-wrap", marginTop: 6 }}>
              {JSON.stringify(selected.properties, null, 2)}
            </pre>
          </div>
        ) : (
          <div>
            <div style={label}>Stats</div>
            <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
              Equipment: {(layout.electrical_equipment || []).length} ·
              Feeders: {(layout.feeders || []).length} ·
              Panels: {(layout.panels || []).length}
            </div>
          </div>
        )}

        <div>
          <div style={label}>Validation · {validation.length}</div>
          <div data-testid="sld-validation-list" style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto" }}>
            {validation.length === 0 && <div style={{ color: "#22c55e", fontSize: 11 }}>✓ No issues found</div>}
            {validation.map((v, i) => (
              <div key={i} style={{ padding: 6, fontSize: 10, borderRadius: 3,
                background: v.severity === "error" ? "rgba(239,68,68,.10)" : "rgba(245,158,11,.10)",
                color: v.severity === "error" ? "#ef4444" : "#f59e0b" }}>
                {v.message}
              </div>
            ))}
          </div>
        </div>

        {err && <div style={{ padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}

function SLDSymbol({ item, selected, onClick, onMouseDown }) {
  const x = item.x, y = item.y;
  const cat = item.category;
  const draw = () => {
    if (cat === "transformer") {
      return <g><circle cx={-12} r={14} fill="#fff" stroke="#222" strokeWidth={1.5} />
                <circle cx={12} r={14} fill="#fff" stroke="#222" strokeWidth={1.5} /></g>;
    }
    if (cat === "load") {
      return <g><circle r={16} fill="#fff" stroke="#222" strokeWidth={1.5} />
                <text x={0} y={5} fontSize={14} textAnchor="middle">M</text></g>;
    }
    if (cat === "source") {
      return <g><circle r={16} fill="#fff" stroke="#222" strokeWidth={1.5} />
                <text x={0} y={5} fontSize={14} textAnchor="middle">G</text></g>;
    }
    if (cat === "breaker") {
      return <g><line x1={0} y1={-15} x2={0} y2={15} stroke="#222" strokeWidth={2} />
                <line x1={-8} y1={0} x2={8} y2={0} stroke="#222" strokeWidth={2} />
                <rect x={-10} y={-12} width={20} height={24} fill="none" stroke="#222" strokeWidth={1} /></g>;
    }
    if (cat === "panel") {
      return <rect x={-30} y={-20} width={60} height={40} fill="#fff" stroke="#222" strokeWidth={2} />;
    }
    if (cat === "bus") {
      return <rect x={-30} y={-2} width={60} height={4} fill="#222" />;
    }
    return <rect x={-15} y={-15} width={30} height={30} fill="#fff" stroke="#222" strokeWidth={1.5} />;
  };
  return (
    <g transform={`translate(${x},${y})`} onClick={onClick} onMouseDown={onMouseDown}
       data-testid={`sld-item-${item.id}`}
       style={{ cursor: "move" }}>
      {draw()}
      {selected && <rect x={-35} y={-30} width={70} height={60} fill="none" stroke="#F26B1F" strokeWidth={2} strokeDasharray="4,2" />}
      <text x={0} y={36} fontSize={11} textAnchor="middle" fontFamily="monospace" fill="#222">{item.tag}</text>
    </g>
  );
}
